import { ROLES } from "core/constants";

export const DURATION_1_SECOND = 1000;
export const DURATION_1_MIN = 60 * DURATION_1_SECOND;
export const DURATION_5_MIN = 5 * DURATION_1_MIN;
export const DURATION_25_MIN = 25 * DURATION_1_MIN;
export const DURATION_30_MIN = 30 * DURATION_1_MIN;

// leverage a luhn validator for npi numbers.
// more info here: https://www.cms.gov/Regulations-and-Guidance/Administrative-Simplification/NationalProvIdentStand/Downloads/NPIcheckdigit.pdf
export const validateNPI = (npi) => {
  let tmp;
  let sum;
  let i = npi.length;
  let j;
  if (i === 15 && npi.slice(0, 5) === "80840") sum = 0;
  else if (i === 10) sum = 24;
  else return false;
  j = 0;
  while (i !== 0) {
    tmp = npi.charCodeAt(i - 1) - "0".charCodeAt(0);
    if (j++ % 2 !== 0) {
      if ((tmp <<= 1) > 9) {
        tmp -= 10;
        tmp++;
      }
    }
    sum += tmp;
    i--;
  }
  if (sum % 10 === 0) return true;
  else return false;
};

export const validateZip = (value) => /(^\d{5}$)|(^\d{5}-\d{4}$)/.test(value);

export const newShade = (hexColor, magnitude) => {
  hexColor = hexColor.replace("#", "");
  if (hexColor.length === 6) {
    const decimalColor = parseInt(hexColor, 16);
    let r = (decimalColor >> 16) + magnitude;
    r > 255 && (r = 255);
    r < 0 && (r = 0);
    let g = (decimalColor & 0x0000ff) + magnitude;
    g > 255 && (g = 255);
    g < 0 && (g = 0);
    let b = ((decimalColor >> 8) & 0x00ff) + magnitude;
    b > 255 && (b = 255);
    b < 0 && (b = 0);
    return `#${(g | (b << 8) | (r << 16)).toString(16)}`;
  } else {
    return hexColor;
  }
};

export const ENVIRONMENTS = {
  LOCAL: "local",
  TEST: "testing",
  DEV: "development",
  QA: "qa",
  PROD: "production",
};

export const getEnv = () => {
  switch (window.location.hostname) {
    case "populi-ui-test.web.app":
    case "populi-ui-test.firebaseapp.com":
    case "test-app.populi.ai":
      return ENVIRONMENTS.TEST;

    case "populi-ui-qa.web.app":
    case "populi-ui-qa.firebaseapp.com":
    case "qa-app.populi.ai":
      return ENVIRONMENTS.QA;

    case "populi-ui-prod.web.app":
    case "populi-ui-prod.firebaseapp.com":
    case "app.populi.ai":
      return ENVIRONMENTS.PROD;

    case "populi-ui-dev.web.app":
    case "populi-ui-dev.firebaseapp.com":
    case "dev-app.populi.ai":
      return ENVIRONMENTS.DEV;

    case "localhost":
    default:
      return (
        ENVIRONMENTS[import.meta.env.REACT_APP_ENVIRONMENT] ||
        ENVIRONMENTS.LOCAL
      );
  }
};

export const getApiUrlPrefix = () => {
  const env = getEnv();

  switch (env) {
    case ENVIRONMENTS.TEST:
      return "https://test-api.populi.ai";

    case ENVIRONMENTS.QA:
      return "https://qa-api.populi.ai";

    case ENVIRONMENTS.PROD:
      return "https://api.populi.ai";

    case ENVIRONMENTS.DEV:
    default:
      return "https://dev-api.populi.ai";
  }
};

export const objectWithFilteredKeys = (obj, ignoredKeys) => {
  const keys = Object.keys(obj);
  const allowedKeys = keys.filter((key) => !ignoredKeys.includes(key));

  return allowedKeys.reduce((result, key) => {
    const isValueObject =
      obj[key] && !Array.isArray(obj[key]) && typeof obj[key] === "object";

    if (isValueObject) {
      result[key] = objectWithFilteredKeys(obj[key], ignoredKeys);
    } else {
      result[key] = obj[key];
    }

    return result;
  }, {});
};

export const objectWithTranslatedKeys = (object, dictionary) => {
  return Object.entries(object).reduce((object, entry) => {
    let [key, value] = entry;

    const isValueObject =
      value && !Array.isArray(value) && typeof value === "object";

    if (isValueObject) {
      value = objectWithTranslatedKeys(value, dictionary);
    }

    if (dictionary[key]) {
      object[dictionary[key]] = value;
    } else {
      object[key] = value;
    }

    return object;
  }, {});
};

export const friendlyNumber = (value) => {
  // make it "human readable" by adding a suffix, 1000 -> 1k, 1000000 -> 1m, etc.
  const symbolMap = [
    { value: 1, symbol: "" },
    { value: 1e3, symbol: "k" },
    { value: 1e6, symbol: "M" },
  ];
  const regex = /\.0+$|(\.[0-9]*[1-9])0+$/;

  let i;
  for (i = symbolMap.length - 1; i > 0; i--) {
    if (value >= symbolMap[i].value) {
      break;
    }
  }

  return (
    (Math.round(value) / symbolMap[i].value).toFixed(1).replace(regex, "$1") +
    symbolMap[i].symbol
  );
};

export const formatSpecialCharacters = (value) => {
  let formattedValue = "";

  for (let i = 0; i < value.length; i++) {
    if (/[().,;:+@-_%']/g.test(value[i])) {
      const val = "\\" + value[i]; // escaping special characters for matching
      formattedValue = formattedValue + val;
    } else {
      formattedValue = formattedValue + value[i];
    }
  }

  return formattedValue;
};

export const hasPermissions = (
  userPermissions,
  requiredPermission,
  dynamicParams = {}
) => {
  // Bypass permissions check if no rules have been passed for requiring permissions
  if (!requiredPermission) {
    return true;
  }

  // If permission is requires (checkRules) then immediately return false for missing the user role's permissions
  if (!userPermissions) {
    return false;
  }

  // Iterate over user role's permissions to find at least one permission
  return userPermissions.some((permission) => {
    try {
      // If the permission requires a dynamic check, it will be an object
      // Example:
      // {
      //   name: "organization:read",
      //   rule: ({ activeMembershipId, orgIdParam }) => {
      //     return activeMembershipId === orgIdParam;
      //   },
      // },
      if (typeof permission === "object") {
        // First check if the name of permission is in fact the requiredPermission to check
        if (permission.name === requiredPermission) {
          return permission.rule(dynamicParams);
        }
        return false;
      }

      return requiredPermission === permission;
    } catch (error) {
      console.error("There was an issue checking user permissions.", {
        userPermissions,
        requiredPermission,
        dynamicParams,
      });
      return false;
    }
  });
};

// Accepts either the role key ("systemAdmin") or its display label ("System
// Admin"), since memberships carry the key while the active membership carries
// the label.
export const isSystemAdmin = (role) =>
  role === "systemAdmin" || role === ROLES.systemAdmin;

export const hasAnalyticsHubUserAccess = (membership) => {
  if (!membership) {
    return false;
  }

  // System Admin is the one role the API does not gate on an access level, so
  // it has access whatever the membership carries.
  if (isSystemAdmin(membership.role)) {
    return true;
  }

  // Every other role, System Viewer included, needs an assigned level that is
  // not "none".
  return membership.analytics_hub_access
    ? membership.analytics_hub_access !== "none"
    : false;
};

export const checkUserIsInternal = (email) => {
  const domain = email.split("@").reverse()[0];
  return ["definitivehc.com", "populi.ai", "monocl.com"].includes(domain);
};

export const checkUserIsDefinitive = (email) => {
  const domain = email.split("@").reverse()[0];
  return ["definitivehc.com"].includes(domain);
};
